import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { Layout } from '../../components/Layout';
import { PageHead } from '../../components/PageHead';
import {
  fetchHelpArticle,
  recordHelpArticleFeedback,
  recordHelpArticleView,
  type HelpArticleDetail,
  type HelpCategory,
} from '@auctisync/api-client';
import { useTenant } from '../../contexts/TenantContext';
import { supabase } from '../../lib/supabase';

/**
 * Article detail page.
 *
 * Renders markdown body via `marked` (small, fast). We render to HTML on the
 * client; for Phase 1 this is fine because:
 *   * Article bodies are authored by us (or tenants with super_admin/staff
 *     role via RLS) — NOT public UGC, so XSS risk is low
 *   * No code execution, no <script> output from marked by default
 * Future hardening: run through DOMPurify before injection. Noted in DoD.
 */
export function Article() {
  const tenant = useTenant();
  const { slug = '' } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<HelpArticleDetail | null>(null);
  const [category, setCategory] = useState<HelpCategory | null>(null);
  const [html, setHtml] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  // "Was this helpful?" — the same question the app asks, recorded in the same
  // place. Two surfaces asking and only one listening would make the tally a
  // half-truth about which articles are failing readers.
  const [voted, setVoted] = useState<boolean | null>(null);

  useEffect(() => {
    async function load() {
      if (!supabase) {
        setError('Help Center backend is warming up. Try again in a moment.');
        return;
      }
      // In white-label mode the tenant's article wins over the platform one on
      // the same slug. That rule lives in @auctisync/api-client, shared with the
      // native app's in-app help centre — two copies is how one surface starts
      // showing a house's wording while the other shows AuctiSync's.
      const { article: art } = await fetchHelpArticle(supabase, slug, tenant?.id ?? null);
      if (!art) {
        setError('Article not found.');
        return;
      }
      setArticle(art);
      // Sanitize the rendered HTML before injection. Authors are trusted
      // (platform + tenant staff), but tenant-authored markdown on a public
      // page should still never carry script — defense in depth.
      const rendered = await marked.parse(art.body_markdown, { async: true });
      setHtml(DOMPurify.sanitize(rendered));

      // Bump the view_count via the anon-safe RPC; failures are silent.
      void recordHelpArticleView(supabase, art.slug);

      // If the article has a category, load it for the breadcrumb
      if (art.category_id) {
        const catRes = await supabase
          .from('help_categories')
          .select('id, slug, title, description, sort_order, audience')
          .eq('id', art.category_id)
          .maybeSingle();
        if (!catRes.error && catRes.data) {
          setCategory(catRes.data as HelpCategory);
        }
      }
    }
    void load();
  }, [slug, tenant]);

  function vote(helpful: boolean) {
    // Shown as answered immediately: the reader has finished with the question,
    // and a spinner on a thumbs-up is the site talking about itself.
    setVoted(helpful);
    if (supabase && article) {
      void recordHelpArticleFeedback(supabase, article.slug, helpful, tenant?.id ?? null);
    }
  }

  if (error) {
    return (
      <Layout>
        <PageHead
          title="Article not found — AuctiSync Help Center"
          description="This article could not be loaded."
        />
        <section className="mx-auto max-w-3xl px-6 py-16">
          <p style={{ color: 'var(--semantic-error)' }}>{error}</p>
          <p className="mt-4">
            <a
              href="/"
              className="underline-offset-4 hover:underline"
              style={{ color: 'var(--brand-accent)' }}
            >
              ← Back to the Help Center
            </a>
          </p>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHead
        title={article ? `${article.title} — AuctiSync Help Center` : 'AuctiSync Help Center'}
        description={article?.summary ?? 'AuctiSync Help Center article.'}
        canonicalPath={`/a/${slug}`}
      />
      <article className="mx-auto max-w-3xl px-6 pt-12 pb-16">
        <nav className="mb-6 text-sm" style={{ color: 'var(--text-muted)' }}>
          <a
            href="/"
            className="underline-offset-4 hover:underline"
            style={{ color: 'var(--brand-accent)' }}
          >
            Home
          </a>
          {category ? (
            <>
              {' / '}
              <a
                href={`/c/${category.slug}`}
                className="underline-offset-4 hover:underline"
                style={{ color: 'var(--brand-accent)' }}
              >
                {category.title}
              </a>
            </>
          ) : null}
        </nav>
        {!article ? (
          <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
        ) : (
          <>
            <header className="mb-8 border-b pb-8" style={{ borderColor: 'var(--border)' }}>
              <h1
                className="text-4xl font-bold tracking-tight md:text-5xl"
                style={{ color: 'var(--text)' }}
              >
                {article.title}
              </h1>
              <p className="mt-4 text-lg" style={{ color: 'var(--text-muted)' }}>
                {article.summary}
              </p>
              <p
                className="mt-4 text-xs tracking-wide uppercase"
                style={{ color: 'var(--text-muted)' }}
              >
                Updated {new Date(article.updated_at).toLocaleDateString()} · {article.view_count}{' '}
                views
              </p>
            </header>
            {/*
              Trusted content (platform authors + tenant staff only, gated via RLS).
              Follow-on hardening: run through DOMPurify before injection.
            */}
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: html }} />

            <div
              className="mt-10 flex items-center gap-3 rounded-lg border p-4"
              style={{ borderColor: 'var(--border)', background: 'var(--surface-muted)' }}
            >
              <span className="flex-1 text-sm font-semibold" style={{ color: 'var(--text)' }}>
                {voted === null ? 'Was this helpful?' : 'Thanks — noted.'}
              </span>
              {voted === null ? (
                <>
                  <button type="button" onClick={() => vote(true)} style={voteStyle}>
                    Yes
                  </button>
                  <button type="button" onClick={() => vote(false)} style={voteStyle}>
                    No
                  </button>
                </>
              ) : null}
            </div>
          </>
        )}
      </article>
    </Layout>
  );
}

const voteStyle: React.CSSProperties = {
  minHeight: 44,
  minWidth: 60,
  padding: '0 1rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
  fontWeight: 600,
  cursor: 'pointer',
};
